import axios, { AxiosInstance } from 'axios';
import { TemplatesResponse, RenderRequest, RenderResponse, InspectorModel, TemplateSummary, DynamicRenderRequest, DynamicRenderResponse, DataScenariosResponse, FeatureMetricResponse } from 'types/cx-editor';
import { RunContextApp } from 'framework/RCApp';

// Lightweight API client for CX Visual Editor endpoints.
// Uses RCApp session to attach Authorization header.

class CxApiClient {
  private axiosClient: AxiosInstance;
  constructor(private rc: RunContextApp, private baseUrl: string) {
    this.axiosClient = axios.create();
    this.axiosClient.interceptors.request.use((config) => {
      const token = this.rc.session.getCMSAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async getTemplates(): Promise<TemplatesResponse> {
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/templates`);
    return resp.data as TemplatesResponse;
  }

  async getTemplateMeta(templateKey: string, elementKey?: string): Promise<{ templateKey: string; name: string; channel: string; elements: { elementKey: string; displayName: string }[]; scenarios: { label: string; value: string }[]; requestId?: string; }>{
    const params = elementKey ? { elementKey } : {};
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/templates/${encodeURIComponent(templateKey)}`, { params });
    return resp.data;
  }

  async getScenarios(templateKey: string): Promise<{ templateKey: string; scenarios: { value: string; label: string; payload?: any }[]; requestId?: string; }>{
    // Use getTemplateMeta instead since /v1/cx/scenarios doesn't exist
    // Scenarios are returned as part of template metadata
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/templates/${encodeURIComponent(templateKey)}`);
    return {
      templateKey: resp.data.templateKey || templateKey,
      scenarios: resp.data.scenarios || [],
      requestId: resp.data.requestId
    };
  }

  async getInspector(params: { templateKey: string; elementKey: string; hierarchy: string; locale: string; scenario: string; }): Promise<InspectorModel & { requestId?: string }>{
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/inspector`, { params });
    return resp.data;
  }

  async getInspectorConfigs(params: { 
    entityType: string;
    entityId: string;
    pilot_id: string;
    productType: string;
    notificationType: string;
    sectionName: string;
    locale: string;
    templateKey?: string | string[];
  }): Promise<{
    configs: {
      text: Array<{
        component: string;
        configDataType: string;
        configKey: string;
        configLevel: string;
        configRegex: string;
        configType: string;
        configValue: string;
        displayName: string;
        documentation: string;
        isPushedToUAT: boolean;
        possibleValues: any[];
        relatedConfigs: any[];
        section: string;
        source: string;
      }>;
      images: any[];
      colors: any[];
      metadata: any[];
    };
    stringResources: {
      text: any[];
      images: any[];
      colors: any[];
      metadata: any[];
    };
  }> {
    const url = `${this.baseUrl}/v1/cx/inspector`;
    console.log('[CxApiClient] getInspectorConfigs - Making API call to:', url);
    console.log('[CxApiClient] getInspectorConfigs - Params object:', params);
    
    // Ensure all params are strings and not undefined/null, but keep arrays as arrays
    const cleanParams: Record<string, string | string[]> = {};
    Object.keys(params).forEach(key => {
      const value = params[key as keyof typeof params];
      if (value !== undefined && value !== null) {
        // Keep arrays as arrays, convert others to strings
        if (Array.isArray(value)) {
          cleanParams[key] = value;
        } else {
          cleanParams[key] = String(value);
        }
      }
    });
    
    console.log('[CxApiClient] getInspectorConfigs - Cleaned params:', cleanParams);
    
    const resp = await this.axiosClient.get(url, { 
      params: cleanParams,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          const value = params[key];
          if (Array.isArray(value)) {
            // Serialize array without brackets: templateKey=value (for each value)
            value.forEach(v => {
              searchParams.append(key, String(v));
            });
          } else {
            searchParams.append(key, String(value));
          }
        });
        return searchParams.toString();
      }
    });
    console.log('[CxApiClient] getInspectorConfigs - Response:', resp.data);
    return resp.data;
  }

  async renderHTML(request: RenderRequest): Promise<RenderResponse & { requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/render`, request);
    return resp.data;
  }

  async renderDynamic(request: DynamicRenderRequest): Promise<DynamicRenderResponse & { requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/render`, request);
    return resp.data;
  }

  async saveChanges(payload: { templateKey: string; elementKey: string; variant: { hierarchy: string; locale: string; scenario: string }; changes: Partial<InspectorModel>; message?: string; }): Promise<{ versionId: string; auditId?: string; requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/save`, payload);
    return resp.data;
  }

  async save(payload: {
    project_id: string;
    configs: Array<{
      project_id: string;
      config_key: string;
      config_type: string;
      config_value: string;
      config_level: string;
      config_documentation: string;
      config_regex: string;
      config_data_type: string;
      display_name?: string;
      features?: string;
      editable?: boolean;
      is_required?: boolean;
      possible_values?: any[];
      related_configs?: any[];
      source?: string;
      product_type?: string;
      fuel_type?: string;
    }>;
    source_name?: string;
    enable_retry?: boolean;
  }): Promise<{
    success: boolean;
    versionId?: string;
    auditId?: string;
    db_result?: {
      created_count: number;
      updated_count: number;
      results: Array<{
        config_key: string;
        project_id: string;
        action: 'created' | 'updated';
        level: string;
      }>;
    };
    push_result?: {
      ok: boolean;
      total: number;
      total_success: number;
      total_failed: number;
    };
    db_attempts?: number;
    push_attempts?: number;
    error?: string;
  }> {
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/save`, payload);
    return resp.data;
  }

  async toggleOverride(payload: { templateKey: string; elementKey: string; field: { type: 'text'|'color'|'image'|'metadata'; id: string }; action: 'create'|'revert' }): Promise<{ ok: boolean; requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/override`, payload);
    return resp.data;
  }

  async signoff(payload: { templateKey: string; elementKey: string; variant: { hierarchy: string; locale: string; scenario: string }; signedOff: boolean }): Promise<{ ok: boolean; auditId?: string; requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/signoff`, payload);
    return resp.data;
  }

  async getPalette(): Promise<{ groups: Array<{ name: string; tokens: Array<{ id: string; label?: string; hex: string }> }> }>{
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/palette`);
    return resp.data;
  }

  async getDataScenarios(pilot_id: string, fuel_type: string, locale: string): Promise<DataScenariosResponse> {
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/data-scenarios`, { params: { pilot_id, fuel_type, locale } });
    return resp.data;
  }

  async getDataScenariosByTemplate(templateName: string, pilotId: string, forceRefresh?: boolean, fuelType?: string): Promise<DataScenariosResponse> {
    const params: Record<string, string | boolean> = { templateName, pilot_id: pilotId };
    if (forceRefresh) {
      params.force_refresh = true;
    }
    if (fuelType) {
      params.fuel_type = fuelType;
    }
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/data-scenarios`, { params });
    return resp.data;
  }

  async saveStrings(payload: {
    project_id: string;
    strings: Array<{
      project_id: string;
      string_key: string;
      string_value: string;
      locale: string;
      string_level: string;
      product_type?: string;
      fuel_type?: string;
    }>;
    source_name?: string;
  }): Promise<{
    success: boolean;
    versionId?: string;
    auditId?: string;
    db_result?: {
      created_count: number;
      updated_count: number;
      results: Array<{
        string_key: string;
        project_id: string;
        action: 'created' | 'updated';
        level: string;
      }>;
    };
    push_result?: {
      ok: boolean;
      total: number;
      total_success: number;
      total_failed: number;
    };
    db_attempts?: number;
    push_attempts?: number;
    error?: string;
    warning?: string;
  }> {
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/save-strings`, payload);
    return resp.data;
  }

  async getFeatureMetrics(pilotId: string, level: 'LEVEL_1' | 'LEVEL_2'): Promise<FeatureMetricResponse> {
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/dcx-clusters/feature-metric/pilot/${pilotId}`, {
      params: { 
        level,
        clusterReference: 'DEFAULT',
        excludedResolutions: 'KILL'
      }
    });
    return resp.data;
  }

  async getBulkManage(pilotId: string): Promise<any> {
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/dcx-clusters/bulk-manage/pilot/${pilotId}`);
    return resp.data;
  }
}

export function createCxApiClient(rc: RunContextApp, baseUrl: string) {
  return new CxApiClient(rc, baseUrl);
}


